import {
  hitEscape,
  receiveDocumentHeaders,
  newNotificationCounts,
  documentClick,
} from '../actions';
import { addEventListener, document } from '../env/browser';
import { getDocumentUrl } from '../utils/url';
import { fetchHeaders } from '../utils/fetch';
import {
  CHECK_FOR_UPDATES_INTERVAL,
  CHECK_FOR_NOTIFICATION_COUNT_INTERVAL,
} from '../constants';
import type { IEventBus } from '@shore/types';
import type { AppDispatch, AppStore } from '../store';
import type { NotificationCounts } from '../actions';
import EVENT_BUS_EVENTS from '../constants/event-bus-events';
import { BETA_TOGGLES, isBetaToggleEnabled } from '../features/beta/toggles';
import { resolveUpgradeEligibility } from '../features/bookings-upgrade/eligibility';

// Minimal interface for the realtime push client — IApiClient from @shore/types does not type this.
export interface RealtimeChannel {
  bind(eventName: string, fn: (data: unknown) => void): void;
}

export interface RealtimeClient {
  connect(): Promise<string>;
  subscribe(channel: string): RealtimeChannel;
  /**
   * Binds pusher connection-state handlers. Throws if called before a
   * connection exists, so only ever call it inside a resolved `connect`.
   *
   * @returns An unbind function.
   */
  bind(handlers: { onConnected?: () => void }): () => void;
}

// Minimal interface for the notifications API — notificationsV2 is not typed in @shore/types.
interface NotificationsApiClient {
  notificationsV2: {
    unread(params: { merchant_id: string }): Promise<NotificationCounts>;
  };
  realtime: RealtimeClient;
}

export function observeEscKey(store: { dispatch: AppDispatch }): void {
  addEventListener('keydown', (event: Event) => {
    const { key, keyCode } = event as KeyboardEvent;
    if (key === 'Escape' || keyCode === 27) {
      store.dispatch(hitEscape());
    }
  });
}

export function observeDocumentHeaders(store: {
  dispatch: AppDispatch;
}): Promise<void> {
  const url = getDocumentUrl(true);
  const update = () =>
    fetchHeaders(url, ['last-modified', 'date']).then(
      ([lastModified, date]) => {
        store.dispatch(receiveDocumentHeaders({ lastModified, date }));
      }
    );

  setInterval(update, CHECK_FOR_UPDATES_INTERVAL);
  return update();
}

/* istanbul ignore next */
export async function observeUnreadNotificationCount(
  store: { dispatch: AppDispatch },
  { apiClient }: { apiClient: NotificationsApiClient },
  merchantId: string
): Promise<void> {
  const update =
    (
      client: NotificationsApiClient,
      dispatch: AppDispatch,
      action: typeof newNotificationCounts
    ) =>
    async () => {
      if (!document.hidden) {
        // TODO replace with web socket
        const unreadNotifications = await client.notificationsV2.unread({
          merchant_id: merchantId,
        });

        dispatch(action(unreadNotifications));
      }
    };

  setInterval(
    update(apiClient, store.dispatch, newNotificationCounts),
    CHECK_FOR_NOTIFICATION_COUNT_INTERVAL
  );
}

/**
 * Broadcasts the WSB appointment allowance to micro apps as it changes.
 *
 * Watches the store rather than the causes of a change, so any new source only
 * has to dispatch into redux — `observeAppointmentCreatedPush` is the first.
 *
 * Two changes reach it. The initial resolve at bootstrap, which matters because
 * a widget mounted before the shell finished initialising would otherwise hold
 * a create() flag of `null` for the rest of the session; and every refetch after
 * it, which is how a widget already on screen learns the count has moved.
 *
 * Emits the eligibility object, not the raw counts, so the payload matches the
 * `appointmentLimit` flag exactly.
 *
 * @returns The redux unsubscribe function.
 */
export function observeAppointmentUsage(
  store: Pick<AppStore, 'getState' | 'subscribe'>,
  { eventBus }: { eventBus: IEventBus }
): () => void {
  let previous = store.getState().currentMerchant.appointmentUsage;

  return store.subscribe(() => {
    const { currentMerchant } = store.getState();
    const current = currentMerchant.appointmentUsage;

    // The reducer replaces the object, so identity is the change signal.
    if (current === previous) return;
    previous = current;

    const eligibility = resolveUpgradeEligibility(
      currentMerchant,
      isBetaToggleEnabled(BETA_TOGGLES.bookingsUpgrade.key)
    );

    // No allowance is the absence of an upsell, not an update worth sending.
    if (!eligibility) return;

    eventBus.emit(EVENT_BUS_EVENTS.APPOINTMENT_USAGE_UPDATED, eligibility);
  });
}

export const observeDocumentClick = ({
  dispatch,
}: {
  dispatch: AppDispatch;
}): void => {
  document.addEventListener('click', () => {
    dispatch(documentClick());
  });
};

export const websocketListener =
  (apiClient: { realtime: RealtimeClient }) =>
  (merchantId: string) =>
  (channelName: string) =>
  (eventName: string) =>
  (fn: (data: unknown) => void): void => {
    const getChannel = (name: string, id: string) => `${name}=${id}`;

    apiClient.realtime.connect().then((state) => {
      if (state === 'connected') {
        const channel = apiClient.realtime.subscribe(
          getChannel(channelName, merchantId)
        );
        channel.bind(eventName, fn);
      } else {
        // TODO: post error to sentry
        console.log(`Failed to bind to channel ${channelName}`);
      }
    });
  };
